import { findMinBy, mapEvery } from '@seaber/common/lib/collections';
import type { VesselSpeedConsumptionFragment } from '../../graphql';
import {
  BunkerGrade,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
} from '../../graphql';
import type { CurrencyRates } from '../../state/zustand';
import { convertMonetaryValue } from '../../state/zustand';
import type { PortCallBunkerOverride } from './Core';
import type { CalculationConfig } from './ScheduleDataContext';
import type { Vessel, VSCSByTagTypeAndArea, VSCSByTypeAndArea } from './Vessel';
import type { EnrichedSpeedConsumption } from './TimeData';
import { resolveBunkerGradeId } from './Core.resolve-bunker-grade-id';
import type { OrgDefaultBunkerGradeIdsByArea } from '../../lib/org-area-bunker-grade.utils';

/**
 * When a leg gets created, the speed of the vessel is originally fetched/set from the default speed value of ‘All areas’.
 * If the leg duration is altered by the user or ETA updates, the new speed is simply derived from the leg duration,
 * and rounded to the nearest value.
 */

export function getNearestSpeedConsumption({
  vesselArea,
  calculatedHours,
  distance,
  bunkerPriceByGradeAndPortCode,
  targetSpeed,
  type,
  vessel,
  isLoading,
  isCargoEvent,
  currencyRates,
  isExtraPortConsumptionHours,
  portBunkerOverrides,
  orgDefaultBunkerGradeIdsByArea,
  weatherFactor,
}: {
  vesselArea: VesselArea;
  bunkerPriceByGradeAndPortCode: CalculationConfig['bunkerPriceByGradeAndPortCode'];
  calculatedHours: number;
  distance: number;
  targetSpeed: number;
  type: VesselSpeedType;
  vessel: Vessel;
  isCargoEvent?: boolean;
  isLoading?: boolean;
  currencyRates: CurrencyRates | null;
  isExtraPortConsumptionHours?: boolean;
  portBunkerOverrides: readonly PortCallBunkerOverride[] | null;
  orgDefaultBunkerGradeIdsByArea: OrgDefaultBunkerGradeIdsByArea | null;
  weatherFactor?: number | null;
}): EnrichedSpeedConsumption | undefined {
  const { speedConsumptions, speedConsumptionsByTag } = vessel;
  const { bunkerGradeId, tag } = resolveBunkerGradeId({
    portBunkerOverrides,
    vesselArea,
    type,
    isExtraPortConsumptionHours,
    isCargoEvent,
    isLoading,
    speedConsumptionsByTag,
    orgDefaultBunkerGradeIdsByArea,
  });

  if (bunkerGradeId === undefined) {
    return;
  }

  const nodes = getSpeedConsumptionsForTagAndType(
    tag,
    type,
    speedConsumptions,
    speedConsumptionsByTag,
    bunkerGradeId,
  );

  if (!nodes) {
    return;
  }

  const { hit, minSpeed } = getClosestSpeedHit(
    nodes,
    vesselArea,
    bunkerGradeId,
    targetSpeed,
  );

  if (hit === null) {
    return;
  }

  const { fuelCost, bunkerGrade } = calculateFuelCost(
    hit,
    bunkerPriceByGradeAndPortCode,
    currencyRates,
  );

  const matchedSpeed = weatherFactor
    ? Number(hit.speed) * (1 - weatherFactor / 100)
    : Number(hit.speed);
  const consumption = +hit.consumption || 0;

  const actualHours =
    type === VesselSpeedType.PORT
      ? calculatedHours
      : matchedSpeed > 0
        ? distance / matchedSpeed
        : distance / targetSpeed; // Fallback if vessel has 0 leg speed defined for some reason

  /*
   * If the calculated speed is less than min speed of the
   * vessel we assume that extra sea hours are idle time
   */
  const LEG_IDLE_MIN_HOURS = 1;
  const hoursDiff = calculatedHours - actualHours;
  const idleHours =
    minSpeed && targetSpeed < minSpeed && hoursDiff >= LEG_IDLE_MIN_HOURS
      ? hoursDiff
      : 0;

  // calculate the consumption
  const consumedBunker = (actualHours * consumption) / 24;

  // calculate the cost
  const voyageCost = consumedBunker * fuelCost;

  const carbonFactor = bunkerGrade?.carbonFactor
    ? +bunkerGrade.carbonFactor
    : undefined;

  return {
    duration: actualHours,
    distance,
    consumption,
    referenceGrade: bunkerGrade?.gradeName || BunkerGrade.MGO,
    fuelCost,
    consumedBunker,
    voyageCost,
    idleHours,
    carbonFactor,
    gradeId: bunkerGradeId,
    gradeName: bunkerGrade?.gradeName,
    targetArea: vesselArea,
  };
}

function calculateFuelCost(
  hit: VesselSpeedConsumptionFragment,
  bunkerPriceByGradeAndPortCode: CalculationConfig['bunkerPriceByGradeAndPortCode'],
  currencyRates: CurrencyRates | null,
) {
  let fuelCost = 0;

  // get the fuel cost if there is an override if not fetch it from bunkerEx
  const bunkerGrade = hit.bunkerGradeOverrideByBunkerGradeId;
  if (bunkerGrade) {
    const referenceGrade = bunkerGrade.referenceGrade;
    const referencePort = bunkerGrade.referencePort;
    const priceAdjustment = +(bunkerGrade.priceAdjustment ?? 0);
    const bunkerExPrice =
      bunkerPriceByGradeAndPortCode?.get(referenceGrade)?.get(referencePort)
        ?.Price ?? 0;

    const price = +(
      bunkerGrade.price ??
      (referenceGrade && referencePort ? bunkerExPrice + priceAdjustment : 0)
    );

    // convert price to default currency
    fuelCost =
      convertMonetaryValue(
        price,
        bunkerGrade.currency ?? 'USD',
        currencyRates,
      ) ?? 0;
  }
  return { fuelCost, bunkerGrade };
}

/**
 * Get all speed consumption nodes that match a bunker grade with area preference.
 * Returns nodes in order of preference: current area, then ALL_AREAS, then other areas.
 * Removes duplicates by speed value. Sorted by speed ascending.
 */
export function getSpeedConsumptionsForAreaAndBunker(
  nodes: Map<VesselArea | null, VesselSpeedConsumptionFragment[]>,
  vesselArea: VesselArea,
  bunkerGradeId: number,
): VesselSpeedConsumptionFragment[] {
  const otherArea =
    vesselArea === VesselArea.INSIDE_ECA
      ? VesselArea.OUTSIDE_ECA
      : VesselArea.INSIDE_ECA;

  /** First try to match in current area, then ALL_AREAS, then any area */
  const speedPreferences = [
    nodes.get(vesselArea),
    nodes.get(VesselArea.ALL_AREAS),
    nodes.get(otherArea),
  ];

  const allMatching: VesselSpeedConsumptionFragment[] = [];
  const seenSpeeds = new Set<number>();

  for (const speeds of speedPreferences) {
    if (!speeds) continue;

    for (const speedConsumption of speeds) {
      if (
        speedConsumption.bunkerGradeOverrideByBunkerGradeId?.id !==
        bunkerGradeId
      )
        continue;

      const speed = +speedConsumption.speed;
      if (isNaN(speed)) continue;

      // Avoid duplicates by speed value
      if (!seenSpeeds.has(speed)) {
        seenSpeeds.add(speed);
        allMatching.push(speedConsumption);
      }
    }
  }

  return allMatching;
}

export function getClosestSpeedHit(
  nodes: Map<VesselArea | null, VesselSpeedConsumptionFragment[]>,
  vesselArea: VesselArea,
  bunkerGradeId: number,
  targetSpeed: number,
) {
  const allMatching = getSpeedConsumptionsForAreaAndBunker(
    nodes,
    vesselArea,
    bunkerGradeId,
  );

  const hit =
    findMinBy(allMatching, (speedConsumption) =>
      Math.abs(targetSpeed - +speedConsumption.speed),
    ) ?? null;

  const minSpeed = hit?.speed ? +hit.speed : Infinity;

  return { hit, minSpeed };
}

export function getSpeedConsumptionsForTagAndType(
  tag: VesselSpeedTag | 'leg',
  type: VesselSpeedType,
  speedConsumptions: VSCSByTypeAndArea,
  speedConsumptionsByTag: VSCSByTagTypeAndArea,
  bunkerGradeId: number,
) {
  let nodes = (
    tag === 'leg' ? speedConsumptions : speedConsumptionsByTag.get(tag)
  )?.get(type);

  const hasValidNodes = (nds: typeof nodes) =>
    !!nds &&
    // There must be at least one value for the matching bunker grade in the map
    !mapEvery(
      nds,
      (v) =>
        v.filter(
          (c) => c.bunkerGradeOverrideByBunkerGradeId?.id === bunkerGradeId,
        ).length === 0,
    );

  // If there are no values for the tag, use the default tag
  if (!hasValidNodes(nodes)) {
    nodes = speedConsumptionsByTag.get(VesselSpeedTag.DEFAULT)?.get(type);
  }
  // If there are no values for the default tag, use untagged
  if (!hasValidNodes(nodes)) {
    nodes = speedConsumptionsByTag.get(null)?.get(type);
  }
  return nodes;
}
